/**
 * Connector "logo" badges for source/target types shown in the Pipeline
 * Overview flow diagram. No real third-party brand marks are bundled here
 * (trademark/licensing risk) — each connector gets a distinct Lucide icon
 * + brand-ish color so Source/Target nodes are visually distinguishable at
 * a glance instead of all sharing one generic icon.
 */

import { Boxes, Cloud, Database, Server, type LucideIcon } from "lucide-react";

export interface ConnectorIconInfo {
  icon: LucideIcon;
  colorClass: string;
}

const FALLBACK: ConnectorIconInfo = {
  icon: Database,
  colorClass: "text-brand-400 bg-brand-500/10 border-brand-500/20",
};

const CONNECTOR_ICONS: Record<string, ConnectorIconInfo> = {
  mssql: {
    icon: Server,
    colorClass: "text-red-400 bg-red-500/10 border-red-500/20",
  },
  sqlserver: {
    icon: Server,
    colorClass: "text-red-400 bg-red-500/10 border-red-500/20",
  },
  postgres: {
    icon: Database,
    colorClass: "text-sky-400 bg-sky-500/10 border-sky-500/20",
  },
  postgresql: {
    icon: Database,
    colorClass: "text-sky-400 bg-sky-500/10 border-sky-500/20",
  },
  mysql: {
    icon: Database,
    colorClass: "text-orange-400 bg-orange-500/10 border-orange-500/20",
  },
  oracle: {
    icon: Database,
    colorClass: "text-red-500 bg-red-600/10 border-red-600/20",
  },
  databricks: {
    icon: Boxes,
    colorClass: "text-orange-500 bg-orange-600/10 border-orange-600/20",
  },
  snowflake: {
    icon: Cloud,
    colorClass: "text-cyan-300 bg-cyan-400/10 border-cyan-400/20",
  },
};

/** Looks up a connector badge by type string (case-insensitive); falls back to a generic database icon. */
export function getConnectorIcon(type: string): ConnectorIconInfo {
  return CONNECTOR_ICONS[type.toLowerCase()] ?? FALLBACK;
}
